import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '@material-ui/core/Button';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import {
  apiList,
  verifyLogin,
  validateLoanDetails,
  validatePropertyDetails,
} from '../../helpers';
import FormLoan from './FormLoan';
import FormLoanProperty from './FormLoanProperty';
import { AddReview, EditReview, loanChanges } from './LoanReview';
import Loading from '../../components/loading/loading';
import SidePanel from '../../components/SidePanel/SidePanel';
import '../../assets/style/loanForm.scss';

// Add / Edit loan: one form in a wide side panel (customer, loan, property,
// address). Same fields, checks and save call as before: Review runs the loan
// checks, then the property checks; a failed check is also shown on its
// field (and scrolled to), next to the usual toast. Passing the checks opens
// a read-only review (Add: key facts + GPS pin; Edit: only what changed)
// and the save happens from there.

// Which field each validation message (helpers/validate.js) is about.
const FIELD_OF = {
  'Loan Type is required': 'loanTypeId',
  'Branch IFSC is required': 'branchIFSC',
  'Loan Number should not be empty.': 'loanNumber',
  'First name should not be empty.': 'custFirstName',
  'Last name should not be empty.': 'custLastName',
  'Customer mobile should not be empty.': 'custMobile',
  'Sanctioned amount should not be empty.': 'sanctionedAmount',
  'Sanctioned Date should not be empty.': 'sanctionedDate',
  'LTV should not be empty.': 'ltv',
  'Built Up Area should not be empty.': 'builtUpArea',
  'Please select structure type.': 'structureType',
  'No of floors should not be empty.': 'noOfFloors',
  'Invalid latitude': 'lat',
  'Invalid longitude': 'lng',
};

// Short text under the field (the toast keeps the full message).
const INLINE = {
  loanTypeId: 'Select a loan type',
  branchIFSC: 'Select a branch',
  loanNumber: 'Enter the loan number',
  custFirstName: 'Enter the first name',
  custLastName: 'Enter the last name',
  custMobile: 'Enter the mobile number',
  sanctionedAmount: 'Enter the sanctioned amount',
  sanctionedDate: 'Pick the sanctioned date',
  ltv: 'Enter the LTV',
  builtUpArea: 'Enter the built-up area',
  structureType: 'Select a structure type',
  noOfFloors: 'Select the number of floors',
  lat: 'Invalid latitude (e.g. 12.9716)',
  lng: 'Invalid longitude (e.g. 77.5946)',
};

export default function OnBoardingDialog({
  open, handleClose, loanId, loanTypes,
}) {
  const [loading, setLoading] = useState(false);
  const [newLoan, setNewLoan] = useState({ });
  const [newProperty, setNewProperty] = useState({});
  const [errors, setErrors] = useState({});
  const [reviewing, setReviewing] = useState(false);
  // The loan as loaded, to list what an edit changes.
  const [original, setOriginal] = useState({});

  // Editing a field clears the inline error (it is re-checked on Save).
  const editLoan = (data) => { setErrors({}); setNewLoan(data); };
  const editProperty = (data) => { setErrors({}); setNewProperty(data); };

  const showError = (message) => {
    toast.error(message);
    const field = FIELD_OF[message];
    if (field) {
      setErrors({ [field]: INLINE[field] || message });
      setTimeout(() => {
        const el = document.getElementById(field);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          el.focus({ preventScroll: true });
        }
      }, 50);
    }
  };

  const saveLoan = () => {
    setLoading(true);
    const { session, user } = verifyLogin();
    const { subscriberId } = user;
    const params = { ...newLoan, ...newProperty };
    const config = { headers: { session, permissionConfig: 'Loan', feature: params.id ? 'edit' : 'add' } };
    params.propertyId = newProperty.id;
    params.id = newLoan.id;
    params.custName = `${newLoan.custFirstName} ${newLoan.custLastName}`;
    params.subscriberId = newLoan.subscriberId || Number(subscriberId);
    axios.post(`${params.id ? apiList.UPDATE_LOAN : apiList.CREATE_LOAN}`, params, config)
      .then(() => {
        setLoading(false);
        handleClose(true);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(`${error.response.data.error || error.response.data.message}`);
      });
  };

  const toTop = () => {
    const form = document.querySelector('.lf-form');
    if (form && form.parentElement) form.parentElement.scrollTop = 0;
  };

  const handleReview = () => {
    const error = validateLoanDetails(newLoan) || validatePropertyDetails(newProperty);
    if (error) {
      showError(error);
    } else {
      setErrors({});
      setReviewing(true);
      setTimeout(toTop, 0);
    }
  };

  const backToForm = () => {
    setReviewing(false);
    setTimeout(toTop, 0);
  };

  const changes = loanId && reviewing ? loanChanges({
    loan: newLoan, property: newProperty, original, loanTypes,
  }) : [];

  useEffect(() => {
    if (loanId) {
      setLoading(true);
      const { session } = verifyLogin();
      const config = { headers: { session, permissionConfig: 'Loan', feature: 'view' } };
      axios.get(`${apiList.VEIW_CUSTOMERS_DETAILS_API}/${loanId}`, config)
        .then((response) => {
          setLoading(false);
          setNewLoan({ ...response.data.data });
          setNewProperty({ ...response.data.data?.LoanProperty });
          setOriginal({
            loan: { ...response.data.data },
            property: { ...response.data.data?.LoanProperty },
          });
        })
        .catch((error) => {
          setLoading(false);
          console.log(error);
          setNewLoan({});
        });
    }
  }, [loanId]);

  return (
    <SidePanel
      open={open}
      setOpen={(value) => { if (!value) handleClose(); }}
      title={`${loanId ? 'Edit loan' : 'Add loan'}${reviewing ? ' — review' : ''}`}
      width={1000}
      flush
      hideHeader
      body={(
        <div className='lf-form'>
          {loading ? <Loading /> : null}
          <ToastContainer />
          {/* The form stays mounted under the review, so going back keeps
              the branch search box filled. */}
          <div className='lf-form-fields' style={{ display: reviewing ? 'none' : 'contents' }}>
            <FormLoan
              newLoan={newLoan}
              setNewLoan={editLoan}
              loanTypes={loanTypes}
              errors={errors}
            />
            <FormLoanProperty
              newProperty={newProperty}
              setNewProperty={editProperty}
              errors={errors}
            />
          </div>
          {reviewing && !loanId ? (
            <AddReview loan={newLoan} property={newProperty} loanTypes={loanTypes} />
          ) : null}
          {reviewing && loanId ? <EditReview changes={changes} property={newProperty} /> : null}
        </div>
      )}
      footer={(
        <div className='lf-footer'>
          {reviewing ? (
            <Button
              className='lf-footer-cancel'
              variant='outlined'
              color='primary'
              startIcon={<ChevronLeftRoundedIcon />}
              onClick={backToForm}
              disabled={loading}
            >
              Back to edit
            </Button>
          ) : (
            <Button className='lf-footer-cancel' color='primary' onClick={() => handleClose()}>
              Cancel
            </Button>
          )}
          {reviewing ? (
            <Button
              variant='contained'
              color='primary'
              disableElevation
              onClick={saveLoan}
              disabled={loading || Boolean(loanId && !changes.length)}
            >
              {loanId ? 'Save changes' : 'Confirm & add loan'}
            </Button>
          ) : (
            <Button
              variant='contained'
              color='primary'
              disableElevation
              onClick={handleReview}
              disabled={loading}
            >
              {loanId ? 'Review changes' : 'Review'}
            </Button>
          )}
        </div>
      )}
    />
  );
}
